/**
 * Admin Entry Editor — View time entry detail with GPS location data.
 * Sprint 15, SDD §10.3, FR-GEO-04.
 *
 * Displays captured coordinates (if available) for investigation purposes.
 * Location data is informational only and never gates punches.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { apiFetch } from '../../lib/api';
import AppLayout from '../../components/layout/AppLayout';

// ── Types ────────────────────────────────────────────────────────────────

interface LocationPoint {
  lat: number;
  lng: number;
  distance_meters: number | null;
  at_station: boolean | null;
}

interface EntryLocation {
  clock_in: LocationPoint | null;
  clock_out: LocationPoint | null;
  station: { lat: number; lng: number; radius_meters: number } | null;
}

interface EntryDetail {
  id: number;
  member_id: number;
  display_name: string;
  email: string;
  battalion_id: number | null;
  battalion_name: string | null;
  category: string;
  position: string | null;
  apparatus: string | null;
  clock_in: string;
  clock_out: string | null;
  duration_minutes: number | null;
  duty_battalion_id: number | null;
  shift_type: string;
  notes: string | null;
  is_voided: number;
  source: string;
  submitted_by: number | null;
  created_at: string;
  updated_at: string;
  location: EntryLocation;
}

// ── Helpers ──────────────────────────────────────────────────────────────

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function formatDuration(minutes: number | null): string {
  if (minutes === null) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function formatLabel(val: string): string {
  return val
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// ── Component ────────────────────────────────────────────────────────────

export default function EntryEditor() {
  const { id } = useParams<{ id: string }>();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [entry, setEntry] = useState<EntryDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!id) return;
    async function fetchEntry() {
      setLoading(true);
      try {
        const data = await apiFetch<EntryDetail>(`/api/admin/entry/${id}`);
        setEntry(data);
      } catch {
        setError('Failed to load entry details.');
      } finally {
        setLoading(false);
      }
    }
    fetchEntry();
  }, [id]);

  if (authLoading || loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-surface-base text-content-secondary">
        Loading...
      </div>
    );
  }
  if (!user || user.role !== 'admin') return null;

  if (error || !entry) {
    return (
      <AppLayout title="Entry Detail">
        <p className="text-red-300 text-center p-8">{error ?? 'Entry not found.'}</p>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Entry Detail">
      {/* Member info */}
      <Section title="Member">
        <Row label="Name" value={entry.display_name} />
        <Row label="Email" value={entry.email} />
        <Row label="Battalion" value={entry.battalion_name ?? '—'} />
      </Section>

      {/* Entry details */}
      <Section title="Time Entry">
        <Row label="Category" value={formatLabel(entry.category)} />
        <Row label="Position" value={entry.position ? formatLabel(entry.position) : '—'} />
        <Row label="Apparatus" value={entry.apparatus ?? '—'} />
        <Row label="Shift Type" value={formatLabel(entry.shift_type)} />
        <Row label="Clock In" value={formatDateTime(entry.clock_in)} />
        <Row label="Clock Out" value={entry.clock_out ? formatDateTime(entry.clock_out) : 'Active'} />
        <Row label="Duration" value={formatDuration(entry.duration_minutes)} />
        <Row label="Source" value={entry.source} />
        {entry.notes && <Row label="Notes" value={entry.notes} />}
        {entry.is_voided === 1 && <Row label="Status" value="VOIDED" valueClassName="text-red-500 font-bold" />}
      </Section>

      {/* Location data — Sprint 15, FR-GEO-04 */}
      <Section title="Location (GPS)">
        {!entry.location.clock_in && !entry.location.clock_out ? (
          <p className="text-content-tertiary italic m-0">No location data captured for this entry.</p>
        ) : (
          <>
            {entry.location.clock_in && <LocationBlock label="Clock In" point={entry.location.clock_in} />}
            {entry.location.clock_out && <LocationBlock label="Clock Out" point={entry.location.clock_out} />}
            {entry.location.station && (
              <div className="mt-3 text-xs text-content-tertiary">
                Station: {entry.location.station.lat.toFixed(4)}, {entry.location.station.lng.toFixed(4)} (radius:{' '}
                {entry.location.station.radius_meters}m)
              </div>
            )}
          </>
        )}
      </Section>
    </AppLayout>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-6">
      <h2 className="text-sm font-semibold text-content-tertiary uppercase tracking-wider mb-3 pb-2 border-b border-edge-muted">
        {title}
      </h2>
      <div className="flex flex-col gap-1.5">{children}</div>
    </div>
  );
}

function Row({ label, value, valueClassName }: { label: string; value: string; valueClassName?: string }) {
  return (
    <div className="flex justify-between items-baseline py-1">
      <span className="text-sm text-content-secondary">{label}</span>
      <span className={`text-sm text-content-primary font-medium ${valueClassName ?? ''}`}>{value}</span>
    </div>
  );
}

function LocationBlock({ label, point }: { label: string; point: LocationPoint }) {
  const atStationLabel =
    point.at_station === true ? 'At station' : point.at_station === false ? 'Away from station' : '—';

  const statusColor =
    point.at_station === true
      ? 'text-green-500'
      : point.at_station === false
        ? 'text-amber-400'
        : 'text-content-tertiary';

  return (
    <div className="p-2.5 bg-surface-raised rounded-lg border border-edge-muted mb-2">
      <div className="text-xs font-semibold text-action-primary mb-1.5">{label}</div>
      <div className="flex justify-between items-baseline py-0.5">
        <span className="text-xs text-content-secondary">Coordinates:</span>
        <span className="text-xs text-content-primary">
          {point.lat.toFixed(6)}, {point.lng.toFixed(6)}
        </span>
      </div>
      {point.distance_meters != null && (
        <div className="flex justify-between items-baseline py-0.5">
          <span className="text-xs text-content-secondary">Distance from station:</span>
          <span className="text-xs text-content-primary">{point.distance_meters}m</span>
        </div>
      )}
      <div className="flex justify-between items-baseline py-0.5">
        <span className="text-xs text-content-secondary">Status:</span>
        <span className={`text-xs font-semibold ${statusColor}`}>{atStationLabel}</span>
      </div>
    </div>
  );
}
